import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { Header } from "../../../components/header";
import { CardHeader } from "../../../components/cards";
import { Loading } from "../../../components/loading";
import { Input } from "../../../components/form";
import { Button } from "../../../components/button";
import { formatDisplayDate } from "../../../helpers/date";
import {
  useMyMailbox,
  useCreateMailbox,
  useUpdateMailbox,
  useRemoveMailbox,
  useSyncMailbox,
} from "../hooks";

const HOSTINGER_DEFAULTS = {
  imapHost: "imap.hostinger.com",
  imapPort: 993,
  smtpHost: "smtp.hostinger.com",
  smtpPort: 465,
};

export const MyMailbox: React.FC = () => {
  const { data, loading, refetch } = useMyMailbox();
  const [removeMailbox, { loading: removing }] = useRemoveMailbox();
  const [syncMailbox, { loading: syncing }] = useSyncMailbox();

  const mailbox = data?.myMailbox?.result;

  const { form: createForm, submit: submitCreate, loading: creating } = useCreateMailbox({
    defaultValues: { ...HOSTINGER_DEFAULTS },
    onCompleted: () => refetch(),
  });

  const { form: updateForm, submit: submitUpdate, loading: updating } = useUpdateMailbox({
    id: mailbox?.id ?? 0,
    onCompleted: () => refetch(),
  });

  useEffect(() => {
    if (mailbox) {
      updateForm.setValue("imapHost", mailbox.imapHost);
      updateForm.setValue("imapPort", mailbox.imapPort);
      updateForm.setValue("smtpHost", mailbox.smtpHost);
      updateForm.setValue("smtpPort", mailbox.smtpPort);
      updateForm.setValue("active", mailbox.active);
    }
  }, [mailbox]);

  const handleDisconnect = async () => {
    if (!mailbox) return;
    if (
      !window.confirm(
        "Déconnecter cette boite mail ? Vous ne recevrez plus vos emails dans le CRM."
      )
    ) {
      return;
    }
    const { data } = await removeMailbox({ variables: { id: mailbox.id } });
    if (data?.removeMailbox?.ok) {
      toast.success("Boite mail déconnectée");
      refetch();
    } else {
      toast.error(data?.removeMailbox?.error ?? "Une erreur est survenue");
    }
  };

  const handleSync = async () => {
    try {
      const { data } = await syncMailbox();
      if (data?.syncMailbox?.ok) {
        const count = data.syncMailbox.syncedCount ?? 0;
        toast.success(
          count > 0
            ? `${count} nouvel${count > 1 ? "s" : ""} email${count > 1 ? "s" : ""} importé${count > 1 ? "s" : ""}`
            : "Boite mail à jour, aucun nouvel email"
        );
      } else {
        toast.error(data?.syncMailbox?.error ?? "Une erreur est survenue");
      }
    } catch (error: any) {
      toast.error(error?.message ?? "Une erreur est survenue");
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <Header title="Ma boite mail" subtitle="Connexion IMAP / SMTP" />
      <div className="main-container">
        <div className="card">
          {!mailbox ? (
            <>
              <CardHeader title="Connecter ma boite mail" />
              <div className="w-full">
                <div className="flex flex-wrap gap-3">
                  <Input
                    label="Email"
                    type="email"
                    className="w-full"
                    {...createForm.register("email", { required: true })}
                    placeholder="moi@mondomaine.fr"
                  />
                  <Input
                    label="Mot de passe"
                    type="password"
                    className="w-full"
                    {...createForm.register("password", { required: true })}
                  />
                  <Input
                    label="Hôte IMAP"
                    className="w-full"
                    {...createForm.register("imapHost", { required: true })}
                  />
                  <Input
                    label="Port IMAP"
                    type="number"
                    className="w-full"
                    {...createForm.register("imapPort", { required: true, valueAsNumber: true })}
                  />
                  <Input
                    label="Hôte SMTP"
                    className="w-full"
                    {...createForm.register("smtpHost", { required: true })}
                  />
                  <Input
                    label="Port SMTP"
                    type="number"
                    className="w-full"
                    {...createForm.register("smtpPort", { required: true, valueAsNumber: true })}
                  />
                </div>
                <div className="w-full mt-4">
                  <Button onClick={submitCreate} loading={creating} disabled={creating}>
                    Connecter la boite mail
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <CardHeader title="Paramètres de la boite mail" />
              <div className="w-full">
                <div className="w-full mt-3">
                  <p className="label">Email</p>
                  <input className="input w-full" value={mailbox.email} disabled />
                </div>
                <div className="flex flex-wrap gap-3 mt-3">
                  <Input
                    label="Hôte IMAP"
                    className="w-full"
                    {...updateForm.register("imapHost")}
                  />
                  <Input
                    label="Port IMAP"
                    type="number"
                    className="w-full"
                    {...updateForm.register("imapPort", { valueAsNumber: true })}
                  />
                  <Input
                    label="Hôte SMTP"
                    className="w-full"
                    {...updateForm.register("smtpHost")}
                  />
                  <Input
                    label="Port SMTP"
                    type="number"
                    className="w-full"
                    {...updateForm.register("smtpPort", { valueAsNumber: true })}
                  />
                  <Input
                    label="Nouveau mot de passe (optionnel)"
                    type="password"
                    className="w-full"
                    {...updateForm.register("password")}
                    placeholder="Laisser vide pour ne pas changer"
                  />
                </div>
                <label className="w-full flex items-center gap-2 mt-3">
                  <input type="checkbox" {...updateForm.register("active")} />
                  <span className="text-sm">Synchronisation active</span>
                </label>
                <div className="w-full mt-3">
                  <p className="label">Dernière synchronisation</p>
                  <input
                    className="input w-full"
                    value={
                      mailbox.lastSyncedAt
                        ? formatDisplayDate(mailbox.lastSyncedAt)
                        : "Jamais synchronisée"
                    }
                    disabled
                  />
                </div>
                <div className="w-full mt-4 flex items-center gap-3">
                  <Button onClick={submitUpdate} loading={updating} disabled={updating}>
                    Enregistrer
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleSync}
                    loading={syncing}
                    disabled={syncing}
                  >
                    Synchroniser maintenant
                  </Button>
                  <span
                    className="text-red-600 text-sm cursor-pointer hover:underline"
                    onClick={handleDisconnect}
                  >
                    {removing ? "Déconnexion..." : "Déconnecter la boite mail"}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
